import { DownloadSimpleIcon, ShareNetworkIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";
import { toast } from "sonner";

import { Button, MiterIcon, Loader } from "ui2";

import { GhsPictogramGrid } from "safety/components/GhsPictogramGrid";
import { HazardRatingsDiamond } from "safety/components/HazardRatingsBadges";
import { PublicSafetyPageContainer, PublicSafetyStatusMessage } from "safety/components/PublicSafetyPage";
import { MiterAPI, SdsNotFoundError } from "safety/miter";

const SdsPage = (): React.JSX.Element => {
  const { token } = useParams<{ token: string }>();
  const {
    data: chemical,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ["public-sds", token],
    queryFn: () => MiterAPI.safety.sds.retrieve(token!),
    enabled: !!token,
    retry: false,
    refetchInterval: 25 * 60 * 1000,
  });

  const copySdsLink = async (): Promise<void> => {
    await navigator.clipboard.writeText(window.location.href);
    toast.success("Copied safety data sheet link!");
  };

  if (isLoading) {
    return (
      <PublicSafetyPageContainer>
        <Loader className="tw:flex-1" size="lg" />
      </PublicSafetyPageContainer>
    );
  }

  if (!token || error instanceof SdsNotFoundError) {
    return (
      <PublicSafetyPageContainer>
        <PublicSafetyStatusMessage
          title="Safety data sheet not found"
          description="This link is invalid or the safety data sheet is no longer available. Double-check the link or ask your supervisor for an updated one."
        />
      </PublicSafetyPageContainer>
    );
  }

  if (!chemical) {
    return (
      <PublicSafetyPageContainer>
        <PublicSafetyStatusMessage
          title="Unable to load safety data sheet"
          description="Something went wrong while loading this safety data sheet. Check your connection and try again."
          action={
            <Button variant="outline" onClick={() => void refetch()} disabled={isRefetching}>
              Try again
            </Button>
          }
        />
      </PublicSafetyPageContainer>
    );
  }

  return (
    <PublicSafetyPageContainer>
      <div className="tw:p-3 tw:flex tw:flex-row tw:items-center tw:gap-3 tw:border-b tw:border-stroke tw:shrink-0">
        <MiterIcon />
        <div className="tw:text-lg tw:font-denim">Miter Safety Data Sheet</div>
        <Button
          className="tw:ml-auto"
          size="icon"
          variant="ghost"
          aria-label="Download safety data sheet"
          onClick={() => window.open(chemical.download_url, "_blank", "noopener,noreferrer")}
        >
          <DownloadSimpleIcon size={24} />
        </Button>
        <Button size="icon" variant="ghost" aria-label="Share safety data sheet" onClick={copySdsLink}>
          <ShareNetworkIcon size={24} />
        </Button>
      </div>
      <div className="tw:p-4 tw:border-b tw:border-stroke tw:shrink-0">
        <div className="tw:flex tw:items-center tw:gap-4">
          <div className="tw:min-w-0 tw:flex-1">
            <h1 className="tw:m-0 tw:text-xl tw:font-denim tw:text-midnight">{chemical.name}</h1>
            {(chemical.manufacturer || chemical.product_code) && (
              <div className="tw:mt-1 tw:flex tw:flex-wrap tw:gap-x-4 tw:gap-y-1 tw:text-sm tw:text-midnight/60">
                {chemical.manufacturer && <span>Manufacturer: {chemical.manufacturer}</span>}
                {chemical.product_code && <span>Product code: {chemical.product_code}</span>}
              </div>
            )}
            <GhsPictogramGrid
              className="tw:mt-3"
              pictogramClassName="tw:size-12"
              values={chemical.ghs_pictograms}
            />
          </div>
          {chemical.hazard_ratings && (
            <div className="tw:size-[72px] tw:shrink-0 tw:mr-3">
              <HazardRatingsDiamond value={chemical.hazard_ratings} />
            </div>
          )}
        </div>
      </div>
      <div className="tw:bg-off-white tw:p-3 tw:min-h-0 tw:flex-1">
        <object
          className="tw:block tw:size-full tw:border tw:border-stroke tw:bg-white"
          data={chemical.pdf_url}
          type="application/pdf"
          title={`${chemical.name} safety data sheet`}
        >
          <div className="tw:flex tw:size-full tw:items-center tw:justify-center tw:p-8 tw:text-center tw:text-midnight/60">
            This browser can’t display the PDF.
          </div>
        </object>
      </div>
    </PublicSafetyPageContainer>
  );
};

export default SdsPage;
